// ─────────────────────────────────────────────────────────────────────────────
// Production Sync Monitor - Real-time sync debugging and monitoring
// Use this component in production to verify sync functionality
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import useBackgroundSync from '../../hooks/useBackgroundSync';
import { useSyncService } from '../../hooks/useSyncService';
import { getOutboxStats, peekOutbox, getEmailSyncStatus, getSedationSyncStatus } from '../../sync/outbox';
import { checkSyncHealth, getDetailedSyncStats } from '../../sync/syncService';
import networkService from '../../hooks/services/networkService';
import { useAuthState } from '../../hooks/providers/AuthProvider';

interface SyncMonitorProps {
  compact?: boolean;
  showDetails?: boolean;
}

export const ProductionSyncMonitor: React.FC<SyncMonitorProps> = ({
  compact = false,
  showDetails = false
}) => {
  const authState = useAuthState();
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const [monitorData, setMonitorData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  // Get status from background sync service (ONLY active sync service)
  const { status: backgroundSyncStatus, triggerManualSync } = useBackgroundSync({
    autoStartOnAuth: false // Don't auto-start, just monitor
  });
  
  // NOTE: Disabled useSyncService to prevent dual sync conflicts
  // const { status: primarySyncStatus } = useSyncService({
  //   autoStart: false // Don't auto-start, just monitor
  // });

  // Monitor network status
  useEffect(() => {
    const updateNetworkStatus = () => setIsOnline(networkService.isOnline());
    
    const cleanup1 = networkService.onOnline(updateNetworkStatus);
    const cleanup2 = networkService.onOffline(updateNetworkStatus);
    
    // Also monitor browser events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      cleanup1?.();
      cleanup2?.();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load comprehensive sync data
  const loadSyncData = async () => {
    setIsLoading(true);
    try {
      const [
        outboxStats,
        outboxRecords,
        emailSyncStatus,
        sedationSyncStatus,
        syncHealth,
        detailedStats
      ] = await Promise.all([
        getOutboxStats(),
        peekOutbox(10), // Get first 10 records
        getEmailSyncStatus(),
        getSedationSyncStatus(),
        checkSyncHealth(),
        getDetailedSyncStats()
      ]);

      setMonitorData({
        outboxStats,
        outboxRecords,
        emailSyncStatus,
        sedationSyncStatus,
        syncHealth,
        detailedStats,
        environment: {
          nodeEnv: process.env.NODE_ENV,
          syncEnabled: process.env.REACT_APP_SYNC_ENABLED !== 'false',
          isElectron: (window as any).electronAPI !== undefined,
          userAgent: navigator.userAgent
        }
      });

      setLastUpdate(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Failed to load sync data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-refresh data
  useEffect(() => {
    loadSyncData();
    const interval = setInterval(loadSyncData, 15000); // Every 15 seconds
    return () => clearInterval(interval);
  }, []);

  // Manual sync trigger
  const handleManualSync = async () => {
    try {
      setIsLoading(true);
      const result = await triggerManualSync();
      console.log('Manual sync result:', result);
      await loadSyncData();
    } catch (error) {
      console.error('Manual sync failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (compact) {
    return (
      <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: '#1f2937',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: 'monospace',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        zIndex: 9999
      }}>
        <div>🔄 Sync: {backgroundSyncStatus.isRunning ? '✅' : '❌'}</div>
        <div>📊 Queue: {backgroundSyncStatus.pendingEvents}</div>
        <div>🌐 Online: {isOnline ? '✅' : '❌'}</div>
        <div>🔑 Auth: {authState.isAuthenticated ? '✅' : '❌'}</div>
        {lastUpdate && <div>⏰ {lastUpdate}</div>}
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: '50px',
      right: '20px',
      width: '400px',
      background: '#1f2937',
      color: 'white',
      padding: '16px',
      borderRadius: '8px',
      fontSize: '12px',
      fontFamily: 'monospace',
      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      zIndex: 9999,
      maxHeight: '80vh',
      overflowY: 'auto'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, color: '#fbbf24' }}>🔄 Sync Monitor</h3>
        <button
          onClick={loadSyncData}
          disabled={isLoading}
          style={{
            background: 'var(--brand-navy)',
            border: 'none',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? '...' : '🔄'}
        </button>
      </div>

      {/* System Status */}
      <div style={{ marginBottom: '12px', background: '#374151', padding: '8px', borderRadius: '4px' }}>
        <div style={{ fontWeight: 'bold', color: '#10b981', marginBottom: '4px' }}>System Status</div>
        <div>🌐 Network: {isOnline ? '✅ Online' : '❌ Offline'}</div>
        <div>🔑 Auth: {authState.isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}</div>
        <div>⚙️ Environment: {monitorData?.environment?.nodeEnv || 'Unknown'}</div>
        <div>🖥️ Platform: {monitorData?.environment?.isElectron ? 'Electron' : 'Browser'}</div>
        <div>⚡ Sync Enabled: {monitorData?.environment?.syncEnabled ? '✅' : '❌'}</div>
      </div>

      {/* Background Sync Status */}
      <div style={{ marginBottom: '12px', background: '#374151', padding: '8px', borderRadius: '4px' }}>
        <div style={{ fontWeight: 'bold', color: 'var(--brand-navy)', marginBottom: '4px' }}>Background Sync</div>
        <div>Status: {backgroundSyncStatus.isRunning ? '✅ Running' : '❌ Stopped'}</div>
        <div>Pending: {backgroundSyncStatus.pendingEvents}</div>
        <div>Processed: {backgroundSyncStatus.totalProcessed}</div>
        <div>Errors: {backgroundSyncStatus.errorCount}</div>
        <div>Last Sync: {backgroundSyncStatus.lastSync ? new Date(backgroundSyncStatus.lastSync).toLocaleTimeString() : 'Never'}</div>
      </div>

            {/* Background Sync Status */}
      <div style={{ marginBottom: '12px', background: '#374151', padding: '8px', borderRadius: '4px' }}>
        <div style={{ fontWeight: 'bold', color: '#06d6a0', marginBottom: '4px' }}>
          🚀 Background Sync Status (PRIMARY & ONLY SYNC SERVICE)
        </div>
        <div>Status: {backgroundSyncStatus.isRunning ? '✅ Running' : '❌ Stopped'}</div>
        <div>Pending Events: {backgroundSyncStatus.pendingEvents}</div>
        <div>Total Processed: {backgroundSyncStatus.totalProcessed}</div>
        <div>Errors: {backgroundSyncStatus.errorCount}</div>
        <div>Last Sync: {backgroundSyncStatus.lastSync ? new Date(backgroundSyncStatus.lastSync).toLocaleTimeString() : 'Never'}</div>
      </div>

      {/* Outbox Statistics */}
      {monitorData?.outboxStats && (
        <div style={{ marginBottom: '12px', background: '#374151', padding: '8px', borderRadius: '4px' }}>
          <div style={{ fontWeight: 'bold', color: '#fbbf24', marginBottom: '4px' }}>📊 Outbox Stats</div>
          <div>Total: {monitorData.outboxStats.total}</div>
          <div>Pending: {monitorData.outboxStats.pending}</div>
          <div>Failed: {monitorData.outboxStats.failed}</div>
          {monitorData.outboxStats.oldestRecord && (
            <div>Oldest: {new Date(monitorData.outboxStats.oldestRecord.enqueuedAt).toLocaleTimeString()}</div>
          )}
        </div>
      )}

      {/* Email/Sedation Sync Status */}
      {(monitorData?.emailSyncStatus || monitorData?.sedationSyncStatus) && (
        <div style={{ marginBottom: '12px', background: '#374151', padding: '8px', borderRadius: '4px' }}>
          <div style={{ fontWeight: 'bold', color: '#06d6a0', marginBottom: '4px' }}>Specific Sync Status</div>
          {monitorData.emailSyncStatus && (
            <>
              <div>📧 Pending Emails: {monitorData.emailSyncStatus.pendingEmails}</div>
              <div>📧 Last Email Sync: {monitorData.emailSyncStatus.lastEmailSync ? new Date(monitorData.emailSyncStatus.lastEmailSync).toLocaleTimeString() : 'Never'}</div>
            </>
          )}
          {monitorData.sedationSyncStatus && (
            <>
              <div>🏥 Pending Sedations: {monitorData.sedationSyncStatus.pendingSedationEvents}</div>
              <div>🏥 Last Sedation Sync: {monitorData.sedationSyncStatus.lastSedationSync ? new Date(monitorData.sedationSyncStatus.lastSedationSync).toLocaleTimeString() : 'Never'}</div>
            </>
          )}
        </div>
      )}

      {/* Health Check */}
      {monitorData?.syncHealth && (
        <div style={{ marginBottom: '12px', background: '#374151', padding: '8px', borderRadius: '4px' }}>
          <div style={{ fontWeight: 'bold', color: monitorData.syncHealth.isHealthy ? '#10b981' : '#ef4444', marginBottom: '4px' }}>
            {monitorData.syncHealth.isHealthy ? '✅' : '❌'} Health Check
          </div>
          {monitorData.syncHealth.issues.length > 0 && (
            <div style={{ color: '#fbbf24' }}>
              Issues: {monitorData.syncHealth.issues.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button
          onClick={handleManualSync}
          disabled={isLoading}
          style={{
            background: '#10b981',
            border: 'none',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          🚀 Manual Sync
        </button>
        <button
          onClick={loadSyncData}
          disabled={isLoading}
          style={{
            background: 'var(--brand-navy)',
            border: 'none',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Recent Outbox Records (expandable) */}
      {showDetails && monitorData?.outboxRecords && monitorData.outboxRecords.length > 0 && (
        <div style={{ marginTop: '12px', background: '#374151', padding: '8px', borderRadius: '4px' }}>
          <div style={{ fontWeight: 'bold', color: '#fbbf24', marginBottom: '4px' }}>Recent Outbox Records</div>
          {monitorData.outboxRecords.slice(0, 5).map((record: any, index: number) => (
            <div key={record.id} style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>
              {index + 1}. {record.operation} - {record.entityType} (attempts: {record.attempts})
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '8px' }}>
        Last updated: {lastUpdate}
      </div>
    </div>
  );
};

export default ProductionSyncMonitor;
